import { type FC, useEffect } from 'react';

import { captureSheetKeys, type CaptureSheetEntry, getCaptureSheet, type HeadlessCaptureJob, readHeadlessCaptureJob } from '@utils/headlessCapture';

/** The page's data must sit still this long before it is captured. */
const SETTLE_MS = 2500;
/** How long a page may take to mount the sheet and load. */
const LOAD_TIMEOUT_MS = 150_000;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const fail = (message: string): void => {
    document.body.dataset.emailRenderError = message;
};

/**
 * Waits for the sheet to be mounted, not loading, and its row count unchanged for SETTLE_MS:
 * a page may render empty before its fetch even starts, so one quiet reading is not enough.
 */
async function settled(key: string): Promise<CaptureSheetEntry> {
    const deadline = Date.now() + LOAD_TIMEOUT_MS;
    let quietSince: number | null = null;
    let lastCount = -1;
    while (Date.now() < deadline) {
        const entry = getCaptureSheet(key);
        if (entry?.handle() && !entry.isLoading()) {
            const count = entry.rowCount();
            if (count !== lastCount) {
                lastCount = count;
                quietSince = Date.now();
            } else if (quietSince !== null && Date.now() - quietSince >= SETTLE_MS) {
                return entry;
            }
        } else {
            quietSince = null;
            lastCount = -1;
        }
        await sleep(250);
    }
    const found = captureSheetKeys();
    throw new Error(getCaptureSheet(key) ? `"${key}" did not finish loading` : `No table "${key}" on this page${found.length ? ` (found: ${found.join(', ')})` : ''}`);
}

async function runJob(job: HeadlessCaptureJob): Promise<void> {
    const entry = await settled(job.page.sheetKey);
    const handle = entry.handle();
    if (!handle) throw new Error('Grid capture handle not mounted');
    if (job.output === 'table') {
        // The attachment is the whole filtered set, like the grid's own download.
        const data = await handle.captureViewData({ maxVisualRows: 1_000_000 });
        const script = document.createElement('script');
        script.id = 'email-render-data';
        script.type = 'application/json';
        script.textContent = JSON.stringify(data).replaceAll('</', '<\\/');
        document.body.appendChild(script);
    } else {
        const result = await handle.captureViewImage({ maxVisualRows: job.rowLimit ?? 30 });
        const img = document.createElement('img');
        img.id = 'email-render-result';
        img.src = result.dataUrl;
        img.hidden = true;
        document.body.appendChild(img);
    }
    document.body.dataset.emailRenderDone = '1';
}

/**
 * Mounted once at the app root. Inert unless the report worker planted a capture job (see
 * `utils/headlessCapture.ts`); then it waits for the named sheet to settle and publishes the capture on the same DOM contract as `/email-render/table`.
 */
const HeadlessCaptureAgent: FC = () => {
    useEffect(() => {
        const job = readHeadlessCaptureJob();
        if (!job) return;
        let started = false;
        // StrictMode runs effects twice in dev; the job must run once.
        const timer = setTimeout(() => {
            if (started) return;
            started = true;
            runJob(job).catch((error: unknown) => fail(error instanceof Error ? error.message : String(error)));
        }, 0);
        return () => clearTimeout(timer);
    }, []);
    return null;
};

export default HeadlessCaptureAgent;
